import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Un menu déroulant qu'on peut fouiller.
 *
 * Un `<Select>` sans recherche tient pour cinq statuts ; pour une flotte de
 * mille camions, il oblige à faire défiler la liste à l'aveugle. Ce composant
 * garde l'allure du déclencheur `SelectTrigger` mais ouvre un panneau avec un
 * champ de recherche en tête.
 *
 * La recherche ignore les accents, la casse, les espaces et la ponctuation :
 * « 376yt » trouve « AF 376 YT KTA », « kone mamadou » trouve « Mamadou
 * Koné ». Chaque mot saisi doit apparaître, dans n'importe quel ordre.
 *
 * Au-delà de `MAX_VISIBLES` résultats, la liste s'arrête et invite à préciser
 * la recherche : on ne monte jamais mille lignes dans le DOM pour un menu.
 *
 * Les options : `{ value, label, description?, keywords?, disabled?,
 * pinned?, className? }`. `label` est ce qu'affiche le déclencheur ;
 * `description` une seconde ligne (chauffeur d'un camion, ville d'une
 * station) ; `keywords` des termes cherchables qu'on n'affiche pas. Une
 * option `pinned` (« + Ajouter une société ») reste en bas de la liste quoi
 * qu'on cherche : une action ne doit pas disparaître parce qu'on a tapé un
 * nom qui n'existe pas encore.
 */
export const MAX_VISIBLES = 100;

export const normaliserRecherche = (texte) =>
  String(texte ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const compacter = (texte) => normaliserRecherche(texte).replace(/[^a-z0-9]/g, '');

const termesCherchables = (option) => {
  const keywords = Array.isArray(option.keywords) ? option.keywords : [option.keywords];
  return [option.label, option.description, ...keywords]
    .filter((terme) => terme !== undefined && terme !== null && terme !== '')
    .join(' ');
};

/**
 * Les options qui répondent à `recherche`, dans leur ordre d'origine, sauf
 * celles dont le libellé commence par la saisie, remontées en tête.
 */
export const filtrerOptions = (options, recherche) => {
  const mots = normaliserRecherche(recherche)
    .split(/\s+/)
    .map(compacter)
    .filter(Boolean);
  if (mots.length === 0) return options;

  const saisie = mots.join('');
  const enTete = [];
  const ensuite = [];
  options.forEach((option) => {
    const botte = compacter(termesCherchables(option));
    if (!mots.every((mot) => botte.includes(mot))) return;
    if (compacter(option.label).startsWith(saisie)) enTete.push(option);
    else ensuite.push(option);
  });
  return [...enTete, ...ensuite];
};

const memeValeur = (a, b) =>
  a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);

const SearchableSelect = ({
  value,
  onValueChange,
  options = [],
  placeholder = 'Choisir…',
  searchPlaceholder = 'Rechercher…',
  emptyMessage = 'Aucun résultat',
  disabled = false,
  id,
  name,
  required = false,
  className,
  contentClassName,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  'aria-invalid': ariaInvalid,
  'data-testid': testId,
  ref,
}) => {
  const [ouvert, setOuvert] = React.useState(false);
  const [recherche, setRecherche] = React.useState('');
  const [surligne, setSurligne] = React.useState('');
  const declencheurRef = React.useRef(null);
  const saisieRef = React.useRef(null);
  const listeRef = React.useRef(null);
  const panneauId = React.useId();

  // La clé interne d'une option est sa position : les valeurs vides ou en
  // double (« Tous les camions » vaut '') ne doivent pas troubler cmdk.
  const entrees = React.useMemo(
    () => options.map((option, position) => ({ ...option, cle: `option-${position}` })),
    [options],
  );
  const choisie = React.useMemo(
    () => entrees.find((option) => memeValeur(option.value, value)) || null,
    [entrees, value],
  );
  const epinglees = React.useMemo(() => entrees.filter((option) => option.pinned), [entrees]);
  const resultats = React.useMemo(
    () => filtrerOptions(entrees.filter((option) => !option.pinned), recherche),
    [entrees, recherche],
  );
  const trouvees = resultats.slice(0, MAX_VISIBLES);
  const masques = resultats.length - trouvees.length;
  const visibles = [...trouvees, ...epinglees];

  // À l'ouverture, l'option déjà choisie est en surbrillance ; dès qu'on
  // tape, c'est le premier résultat, pour qu'Entrée choisisse ce qu'on voit.
  React.useEffect(() => {
    if (!ouvert) return;
    const depart =
      (!recherche && choisie && visibles.find((option) => option.cle === choisie.cle)) ||
      visibles.find((option) => !option.disabled);
    setSurligne(depart ? depart.cle : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, recherche, entrees]);

  // Rouvrir sur le 600e camion doit le montrer, pas le haut de la liste.
  React.useEffect(() => {
    if (!ouvert || !surligne) return;
    const element = listeRef.current?.querySelector('[cmdk-item][data-selected="true"]');
    element?.scrollIntoView?.({ block: 'nearest' });
  }, [ouvert, surligne]);

  const changerOuverture = (suivant) => {
    setOuvert(suivant);
    if (!suivant) setRecherche('');
  };

  const choisir = (option) => {
    if (option.disabled) return;
    onValueChange?.(option.value);
    changerOuverture(false);
    declencheurRef.current?.focus();
  };

  // Taper une lettre sur le déclencheur fermé ouvre la recherche avec cette
  // lettre, comme le fait un <select> natif.
  const auClavier = (event) => {
    if (disabled || ouvert) return;
    if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      setRecherche(event.key);
      setOuvert(true);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOuvert(true);
    }
  };

  const lierDeclencheur = (noeud) => {
    declencheurRef.current = noeud;
    if (typeof ref === 'function') ref(noeud);
    else if (ref) ref.current = noeud;
  };

  const renderOption = (option) => (
    <CommandPrimitive.Item
      key={option.cle}
      value={option.cle}
      disabled={option.disabled}
      onSelect={() => choisir(option)}
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50',
        option.className,
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{option.label}</span>
        {option.description ? (
          <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
        ) : null}
      </span>
      {choisie && choisie.cle === option.cle ? (
        <Check className="absolute right-2 h-4 w-4" aria-hidden="true" />
      ) : null}
    </CommandPrimitive.Item>
  );

  const declencheur = (
    <Popover open={ouvert} onOpenChange={changerOuverture} modal>
      <PopoverTrigger asChild>
        <button
          ref={lierDeclencheur}
          type="button"
          id={id}
          role="combobox"
          aria-expanded={ouvert}
          aria-controls={ouvert ? panneauId : undefined}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          aria-required={required || undefined}
          data-placeholder={choisie ? undefined : ''}
          data-testid={testId}
          disabled={disabled}
          onKeyDown={auClavier}
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-left text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span
            className={cn('min-w-0 flex-1 truncate', !choisie && 'text-muted-foreground')}
            title={choisie && typeof choisie.label === 'string' ? choisie.label : undefined}
          >
            {choisie ? choisie.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-[var(--radix-popover-trigger-width)] min-w-[14rem] p-0', contentClassName)}
        onOpenAutoFocus={(event) => {
          // Le curseur va en fin de saisie : la lettre tapée sur le
          // déclencheur ne doit pas être remplacée par la suivante.
          event.preventDefault();
          const saisie = saisieRef.current;
          if (!saisie) return;
          saisie.focus();
          saisie.setSelectionRange(saisie.value.length, saisie.value.length);
        }}
      >
        <CommandPrimitive
          id={panneauId}
          shouldFilter={false}
          loop
          value={surligne}
          onValueChange={setSurligne}
          label={ariaLabel || placeholder}
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
            <CommandPrimitive.Input
              ref={saisieRef}
              value={recherche}
              onValueChange={setRecherche}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <CommandPrimitive.List
            ref={listeRef}
            className="overflow-y-auto overflow-x-hidden p-1"
            style={{
              maxHeight: 'min(18rem, calc(var(--radix-popover-content-available-height, 21rem) - 3rem))',
            }}
          >
            {trouvees.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
            ) : null}
            {trouvees.map(renderOption)}
            {masques > 0 ? (
              <p className="px-2 py-2 text-xs text-muted-foreground">
                {masques} autre{masques > 1 ? 's' : ''} résultat{masques > 1 ? 's' : ''} : précisez la
                recherche.
              </p>
            ) : null}
            {epinglees.length > 0 ? (
              <>
                <div className="-mx-1 my-1 h-px bg-muted" />
                {epinglees.map(renderOption)}
              </>
            ) : null}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  );

  if (!name && !required) return declencheur;

  // Un champ caché porte la valeur pour un <form> natif : `required` y
  // bloque l'envoi comme sur un <select>, et la bulle d'erreur renvoie le
  // focus au déclencheur.
  return (
    <div className="relative">
      {declencheur}
      <input
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        required={required}
        disabled={disabled}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={() => {}}
        onFocus={() => declencheurRef.current?.focus()}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
    </div>
  );
};

SearchableSelect.displayName = 'SearchableSelect';

export { SearchableSelect };
export default SearchableSelect;
